/**
 * Domain types — the server-authoritative game model.
 *
 * Source of truth for DESIGN §4.1. These types are isomorphic (imported by
 * game-server, control-plane, and web) so the wire contract has exactly one
 * definition. No `any` (AGENT_GUIDE §5).
 *
 * Ported/evolved from the legacy `src/types/index.ts`, which stays untouched.
 */

import type { GameRules, GameRulesOverrides } from './rules';
import type { PowerUpGameState, PowerUpTokens } from './powerups';

/** A board round. `FINAL_JEOPARDY` has no board grid — see {@link FinalJeopardyState}. */
export type Round = 'JEOPARDY' | 'DOUBLE_JEOPARDY' | 'FINAL_JEOPARDY';

/**
 * Top-level game lifecycle phase (DESIGN §4.1 / §8 state machine).
 * `CLUE_OPEN` carries a {@link ClueState} sub-state machine.
 *
 * `LOBBY` is a real, entered waiting room (M12): both modes are born here and
 * enter play via an explicit host action (`start_game` for a pack game,
 * `start_generation` → `GENERATING` → `IN_ROUND` for a dynamic game).
 * `GENERATING` is a dynamic-only phase in which the question-generation agent
 * runs and streams `generation_status`; on success the freshly-built pack
 * snapshot is swapped in and play begins (`IN_ROUND`), on failure/cancel the
 * game returns to `LOBBY`.
 */
export type GamePhase =
  | 'LOBBY'
  | 'GENERATING' // dynamic-only: the generation agent is building the board (M12)
  | 'IN_ROUND' // board play for the current round
  | 'CLUE_OPEN' // a clue is selected; sub-state in ClueState
  | 'FINAL_WAGER'
  | 'FINAL_CLUE'
  | 'FINAL_JUDGING'
  | 'FINAL_REVEAL' // #205: correct-response linger after the last player is judged
  | 'GAME_OVER';

/**
 * Which game mode a game plays (#253/#257 S3). Internal + on the wire from S3, but the
 * only mode that exists today is `JEOPARDY`; S4 adds `TIMED_QUIZ` as a second
 * {@link GameState} arm without reshaping {@link GameStateCore}.
 */
export type GameModeId = 'JEOPARDY';

/**
 * Mode-generic **game lifecycle** (#257 S3, D-S3.3) — the split-out of the old flat
 * {@link GamePhase}'s lifecycle values. `IN_PLAY` is a single core phase covering ALL
 * in-play activity; the mode owns the fine-grained play sub-phase ({@link JeopardyPhase}).
 * This keeps clock-sync + the orchestrator mode-neutral: they gate on `lifecycle === 'IN_PLAY'`.
 */
export type GameLifecycle = 'LOBBY' | 'GENERATING' | 'IN_PLAY' | 'GAME_OVER';

/**
 * Jeopardy's play sub-phase (#257 S3) — the split-out of the old flat {@link GamePhase}'s
 * in-play values, now owned by the Jeopardy mode. Meaningful ONLY while the core
 * {@link GameStateCore.lifecycle} is `IN_PLAY` (undefined otherwise).
 */
export type JeopardyPhase =
  | 'IN_ROUND'
  | 'CLUE_OPEN'
  | 'FINAL_WAGER'
  | 'FINAL_CLUE'
  | 'FINAL_JUDGING'
  | 'FINAL_REVEAL';

/** The `modeVersion` for the Jeopardy sub-contract (#257 S3, D-S3.4). Bumped per-mode. */
export const JEOPARDY_MODE_VERSION = 1;

/**
 * Split a legacy flat {@link GamePhase} into the S3 core-lifecycle + mode-play-phase pair
 * (#257 S3, D-S3.3). The single source of truth for the mapping, reused by the persistence
 * migration shim and by {@link setGamePhase}. The three lifecycle-only values
 * (`LOBBY`/`GENERATING`/`GAME_OVER`) have no play sub-phase.
 */
export function mapLegacyPhase(phase: GamePhase): {
  lifecycle: GameLifecycle;
  jeopardyPhase: JeopardyPhase | undefined;
} {
  switch (phase) {
    case 'LOBBY':
    case 'GENERATING':
    case 'GAME_OVER':
      return { lifecycle: phase, jeopardyPhase: undefined };
    case 'IN_ROUND':
    case 'CLUE_OPEN':
    case 'FINAL_WAGER':
    case 'FINAL_CLUE':
    case 'FINAL_JUDGING':
    case 'FINAL_REVEAL':
      return { lifecycle: 'IN_PLAY', jeopardyPhase: phase };
    default: {
      // Compile-time exhaustiveness without importing `assertNever` from protocol.ts
      // (that would make domain.ts ↔ protocol.ts circular).
      const exhaustive: never = phase;
      throw new Error(`Unknown GamePhase: ${String(exhaustive)}`);
    }
  }
}

/**
 * Reconstruct the legacy flat {@link GamePhase} view from a Jeopardy game's core
 * `lifecycle` + `jeopardy.phase` (#257 S3). Behavior-preserving accessor for the many
 * sites that branch on the combined phase: when `IN_PLAY`, the play sub-phase is the
 * flat phase; otherwise the lifecycle value IS the flat phase.
 */
export function gamePhaseOf(state: {
  lifecycle: GameLifecycle;
  jeopardy: { phase?: JeopardyPhase };
}): GamePhase {
  if (state.lifecycle === 'IN_PLAY') {
    // Invariant (D-S3.3): jeopardy.phase is always set while IN_PLAY.
    return state.jeopardy.phase ?? 'IN_ROUND';
  }
  return state.lifecycle;
}

/**
 * Nullable variant of {@link gamePhaseOf} (#257 S3) for the client selectors whose state may
 * be `null`/`undefined` (not yet connected). Returns `undefined` for a nullish state — exactly
 * what the pre-S3 `state?.phase` yielded — so a comparison like `=== 'CLUE_OPEN'` is preserved.
 */
export function gamePhaseOfOrNull(
  state:
    { lifecycle: GameLifecycle; jeopardy: { phase?: JeopardyPhase } } | null | undefined,
): GamePhase | undefined {
  return state == null ? undefined : gamePhaseOf(state);
}

/**
 * Set a Jeopardy game's phase from a legacy flat {@link GamePhase}, writing BOTH the core
 * `lifecycle` and the mode `jeopardy.phase` together so the D-S3.3 invariant always holds
 * (#257 S3). The single mutation point for phase transitions — replaces `state.phase = X`.
 */
export function setGamePhase(
  state: { lifecycle: GameLifecycle; jeopardy: { phase?: JeopardyPhase } },
  phase: GamePhase,
): void {
  const mapped = mapLegacyPhase(phase);
  state.lifecycle = mapped.lifecycle;
  state.jeopardy.phase = mapped.jeopardyPhase;
}

/**
 * How a game's board is sourced (M12). `PACK` games snapshot an uploaded/BYO pack
 * at creation (the pre-M12 behavior); `DYNAMIC` games are born board-less in
 * `LOBBY` and have a pack generated on the fly at `start_generation`.
 */
export type BoardSource = 'PACK' | 'DYNAMIC';

/**
 * Who runs the game (#91, FR-11.1). `HUMAN` is the default — a real user in the
 * host seat drives `open_buzzer`/`judge`/round flow (all pre-#91 behavior).
 * `AUTO` is the optional **hostless voice mode**: an in-actor auto-host reads each
 * clue aloud, opens the buzzer at a single fair server-decided moment, and an
 * LLM judges the buzz winner's transcribed spoken answer. Fixed at creation and
 * persisted on {@link GameState} so recovery rebuilds identical behavior.
 */
export type HostMode = 'HUMAN' | 'AUTO';

/**
 * One read-aloud chunk of a clue with a dramatic pause after it (#91, D7). The
 * single, transport-agnostic contract the client TTS wrapper consumes, whatever
 * produced it: the dynamic generator emits these for `AUTO` games (the LLM knows
 * where a game-show beat belongs), and the client derives them heuristically
 * (clause/punctuation split) when a clue carries none. Concatenating `text`
 * across the segments reconstructs the clue `prompt` (modulo whitespace), so a
 * client that trusts the segmentation never drops words.
 */
export interface SpeechSegment {
  /** The text of this chunk, read as one utterance. */
  text: string;
  /** Silent pause after this chunk, in milliseconds (0 for the last chunk). */
  pauseMsAfter: number;
}

/**
 * Coarse age band (M12, D3). The ONLY age representation that ever reaches the
 * generation agent or goes on the wire — raw ages / birth years are never
 * transmitted (see M12 §9 privacy). Guests self-report a band at join
 * (this-game-only); a registered player's band is derived server-side from their
 * profile `birthYear` at game start.
 */
export type AgeBand = 'KID' | 'TEEN' | 'ADULT';

/**
 * Ordered stages of the dynamic-generation pipeline (M12 §4.2/§5.2). Streamed to
 * every client via `generation_status` so the lobby renders live progress.
 */
export type GenerationStage =
  | 'SELECTING_CATEGORIES'
  | 'WRITING_CLUES'
  | 'CHECKING_COHERENCY'
  | 'DEDUPING'
  | 'VALIDATING'
  | 'FINALIZING';

/**
 * One player's lobby submission (M12): their wished categories and, optionally,
 * a self-reported age band (guests). Category wishes are public in the lobby;
 * age bands are surfaced to the agent only in aggregate, never broadcast
 * per-player (M12 §4.3 / §9).
 */
export interface CategoryPreference {
  categories: string[];
  ageBand?: AgeBand;
}

/**
 * Zero-commitment demo box (#246, FR-17.4). Present only on a demo game (see
 * {@link GameState.demo}); both bounds are optional and whichever is reached first
 * auto-ends the demo to `GAME_OVER` (→ the convert CTA). Enforced server-side by the
 * owning actor; carried on the wire so the client ribbon can show the countdown /
 * "clue N of C" against the authoritative bounds.
 */
export interface DemoConfig {
  /**
   * Wall-clock deadline (epoch ms). At/after this the demo auto-ends regardless of
   * phase — so an abandoned demo tears itself down (aggressive teardown, FR-17.4).
   * Absent = no time box.
   */
  endsAtEpochMs?: number;
  /**
   * Max clues consumed (across the game) before the demo auto-ends. Absent = no clue
   * box. Kept ≤ one round's clue count so the demo always ends within the first round.
   */
  clueCap?: number;
}

/**
 * Pre-play lobby state (M12), present only in `LOBBY`/`GENERATING`. Holds each
 * player's category-preference submission keyed by `playerId`.
 */
export interface LobbyState {
  /** playerId -> that player's submission (idempotent replace). */
  preferences: Record<string, CategoryPreference>;
}

/**
 * In-flight generation progress (M12), present only in `GENERATING`. Mirrors the
 * latest `generation_status` so a client that connects mid-generation renders the
 * current stage from full state.
 */
export interface GenerationState {
  stage: GenerationStage;
  message: string;
  progress?: { done: number; total: number };
  startedAtEpochMs: number;
  /**
   * Whether a live LLM generation run is actually committed (#346). `true` once the
   * live fallback is running (show the detailed six-step pipeline); absent/`false`
   * while the server is still deciding store-vs-live or assembling a board entirely
   * from the pre-generated ClueStore (show the generic loading state). A store-hit
   * game never sets this `true`, so it no longer flashes the live-pipeline UI.
   */
  live?: boolean;
}

/** A single board cell. The `prompt` is read aloud; `response` is host-only. */
export interface Clue {
  id: string;
  categoryId: string;
  round: Round;
  /** Round-appropriate value (e.g. 200..1000 / 400..2000). */
  value: number;
  /** Shown to all — the "answer" the host reads aloud. */
  prompt: string;
  /** Host-only — the correct "question". Never sent to players/presentation. */
  response: string;
  /** NEW (DESIGN §4.1): hidden Daily Double marker. */
  isDailyDouble: boolean;
  /** Optional per-clue timer override; falls back to pack/default (FR-7.5). */
  timerDuration?: number;
  /**
   * Optional read-aloud segmentation with dramatic pauses (#91, D7). Populated
   * by the dynamic generator for `AUTO` games; absent for pack/uploaded clues
   * (the client TTS wrapper derives pauses heuristically then). Not host-only —
   * carries no answer, so it passes through the role-filtered views like `prompt`.
   */
  speech?: SpeechSegment[];
  /**
   * Optional same-origin path to this clue's pre-generated prompt audio (#99,
   * NFR-6.9), e.g. `/audio/v1/<hash>.mp3`. When present, an auto-host client
   * fetches the cached MP3 directly (no `/api/voice/tts` round-trip on the first
   * read); its absence/failure falls back to on-demand synthesis then the browser
   * voice. Carries no answer, but — unlike `prompt`/`speech` — the role-filtered
   * view (`renderGameState`) exposes it ONLY for the clue currently being read
   * aloud, so audio is never delivered for an unrevealed/unselected/withheld clue.
   */
  audioRef?: string;
}

export interface Category {
  id: string;
  name: string;
  round: Round;
  /** Clue ids in board order (top → bottom by value). */
  clueIds: string[];
}

/** NEW (DESIGN §4.1): a per-round board grid. */
export interface Board {
  round: Round;
  categories: Category[];
  consumedClueIds: string[];
}

/** Final Jeopardy clue content (single category, no board grid). */
export interface FinalJeopardyClue {
  categoryName: string;
  prompt: string;
  response: string;
}

/**
 * NEW (DESIGN §4.1): immutable copy of a pack taken at game creation (FR-8.4).
 * Later pack edits never alter an in-flight game.
 */
export interface PackSnapshot {
  packId: string;
  version: string;
  /** JEOPARDY + DOUBLE_JEOPARDY board grids. */
  boards: Board[];
  cluesById: Record<string, Clue>;
  finalJeopardy: FinalJeopardyClue;
}

export interface Player {
  /** Persistent playerId (guest or registered). */
  id: string;
  /** Per-game seat id. */
  seatId: string;
  name: string;
  /** May be negative (FR-4.7). */
  score: number;
  /** NEW (DESIGN §4.1): presence (FR-2.7). */
  connected: boolean;
  /**
   * NEW (#42, FR-2.10): the player's profile picture as a same-origin URL
   * (`/avatars/<key>`), captured at join for registered players who set one.
   * Absent for guests and registered players without a picture — the UI renders
   * name-only. Not a secret, so it passes cleanly through the role-filtered views.
   */
  avatarUrl?: string;
  /**
   * Power-up hand (#232). Absent kind = 0. Visible to every role (the shared "threat
   * strip" shows who holds what) — it carries no secret. The private Sneak Peek hint
   * is delivered by a targeted `sneak_peek_revealed` event, NOT here.
   */
  tokens?: PowerUpTokens;
}

/** How the currently open clue is answered. */
export type ClueMode = 'BUZZER' | 'DAILY_DOUBLE';

/**
 * Sub-state of an open clue.
 *
 * `READING` and `JUDGING` are auto-host-only (#91): `READING` precedes
 * `BUZZER_OPEN` while the clue is being read aloud (buzzers not yet open);
 * `JUDGING` is the transient after a buzz winner submits a spoken answer and
 * before the LLM verdict lands. Human-hosted games never enter these.
 */
export type ClueStatus =
  'READING' | 'AWAITING_WAGER' | 'BUZZER_OPEN' | 'LOCKED' | 'JUDGING' | 'RESOLVED';

/**
 * The most recent auto-host LLM ruling (#91, D8), retained on {@link GameState}
 * so a Dispute can target it even after the clue was consumed and the board moved
 * on. Replaced by each new ruling; cleared once a dispute over it resolves.
 */
export interface AutoRuling {
  clueId: string;
  playerId: string;
  /** The verdict as applied (`true` = correct). A flip inverts it. */
  correct: boolean;
  /**
   * The score delta this ruling applied (e.g. `+value` / `-value`, or `±wager`
   * for a Daily Double). A dispute flip corrects the score by `-2 * scoreDelta`
   * (undo this delta and apply the opposite). Score-only correction — a flip does
   * not retroactively change board control or un-consume the clue (v1 scope).
   */
  scoreDelta: number;
}

/**
 * An open player vote to override an auto-host ruling (#91, D8). The ruled player
 * is excluded from voting (they're biased); a strict majority of the cast votes
 * to reject flips the ruling, a tie / no-majority / no-votes leaves it standing,
 * and `resolvesAtEpochMs` (a `disputeVoteSec` window) auto-resolves a partial vote.
 * One dispute per ruling. Lives on {@link GameState} (not {@link ClueState}) because
 * a dispute may run after the clue is already consumed.
 */
export interface DisputeState {
  /** The clue whose ruling is under dispute. */
  clueId: string;
  /** The player whose ruling is under dispute (excluded from voting). */
  ruledPlayerId: string;
  /** The ruling being disputed (so a flip is unambiguous). */
  ruledCorrect: boolean;
  /** Score delta to reverse on a successful flip (from {@link AutoRuling}). */
  scoreDelta: number;
  /** Connected players eligible to vote (everyone except the ruled player). */
  eligibleVoterIds: string[];
  /** playerId -> accept(true, ruling stands) / reject(false, flip). Idempotent replace. */
  votes: Record<string, boolean>;
  /** Auto-resolve deadline (a `disputeVoteSec` window). */
  resolvesAtEpochMs: number;
}

/**
 * End-of-clue reveal payload (#192, FR-4.10). Present on {@link ClueState} ONLY
 * while `status === 'RESOLVED'` — the brief hold shown to players + presentation
 * after a clue resolves, before the board returns.
 *
 *  - `unanswered` — nobody got it right (all wrong, timer expired, dismissed, or a
 *    wrong Daily Double). During the hold (`GameRules.revealHoldSec`) the correct
 *    response is revealed to ALL roles — NOT via this payload, but by un-blanking
 *    the active clue's `response` in the role-filtered view (see `views.ts`), so the
 *    leak gate stays in one place. In AUTO mode the client derives the answer's
 *    read-aloud segments from that revealed response and speaks them (Polly).
 *  - `correct` — a player was awarded the points. The response is neither shown nor
 *    read; the client renders an award cue (`awardedPlayerId` + `delta`) and the
 *    board returns after a brief `GameRules.awardHoldSec`.
 *
 * This payload carries NO secret (no response text), so it needs no role filtering —
 * the response reaches non-host clients only through the `views.ts` un-blank, gated
 * on `outcome === 'unanswered'`. `resolvesAtEpochMs` drives the client hold-countdown
 * and lets recovery re-arm the remaining hold.
 */
export interface ClueReveal {
  outcome: 'unanswered' | 'correct';
  /** Correct path: the awarded player (their scoreboard row + self-view pulse). */
  awardedPlayerId?: string;
  /** Correct path: the score delta applied by the award (e.g. `+value`/`+wager`). */
  delta?: number;
  /** Wall-clock (epoch ms) at which the hold ends and the board returns. */
  resolvesAtEpochMs: number;
}

/** NEW (DESIGN §4.1): per-open-clue sub-state machine. */
export interface ClueState {
  clueId: string;
  mode: ClueMode;
  status: ClueStatus;
  /** Player currently holding the buzzer. */
  activePlayerId?: string;
  /** Players who answered incorrectly this clue and may not buzz again (FR-4.8). */
  lockedOutPlayerIds: string[];
  /** DD only: the uncovering player (the only one who may answer). */
  dailyDoublePlayerId?: string;
  /** DD wager, once submitted. */
  wager?: number;
  /**
   * Auto-host (#91) — the fair server-decided buzzer-open moment while `READING`
   * (D1). The buzzer opens at this instant regardless of local voice speed, so
   * every client's window opens together.
   */
  buzzerOpensAtEpochMs?: number;
  /**
   * #264 (FR-4.13) — the scheduled fair-open instant on the SERVER-MONOTONIC clock
   * (`monoNow()`), set when a buzzer open is scheduled (`monoNow() + lookaheadMs`).
   * The server accepts buzzes once `monoNow() ≥ openAtMono` even if the flip job
   * hasn't run yet (B1), and clears this when the open flips, is cancelled, or the clue
   * is consumed. Monotonic ⇒ meaningless after a restart — recovery uses
   * {@link openScheduledAtEpochMs} instead. Server-internal timing; carries no secret.
   */
  openAtMono?: number;
  /**
   * #264 (FR-4.13) — the same scheduled fair-open instant on the WALL clock (epoch ms),
   * persisted so recovery can restore a pending open across a restart (`openAtMono` can't
   * survive one). On recovery: if it has passed, open immediately; else re-arm the open
   * for `openScheduledAtEpochMs − now`. Present only while an open is pending its flip.
   */
  openScheduledAtEpochMs?: number;
  /**
   * Auto-host (#91) — playerIds whose clients have reported `read_complete`
   * (advisory, D1). When all connected players report, the buzzer opens early
   * (capped by `buzzerOpensAtEpochMs`).
   */
  readCompletedBy?: string[];
  /**
   * End-of-clue reveal (#192, FR-4.10). Present ONLY while `status === 'RESOLVED'`:
   * the correct-response hold (unanswered) or the award cue (correct). Cleared when
   * the clue is consumed and the board returns. See {@link ClueReveal}.
   */
  reveal?: ClueReveal;
  /**
   * #232: playerIds who armed **Double Up** on this clue (pre-buzz). If the buzz
   * winner is among them and is judged correct, the award is 2×; a wrong answer is
   * the normal −value. Any arming player who never wins the buzz is refunded when the
   * clue resolves. Cleared when the clue is consumed.
   */
  doubleUpBy?: string[];
  /** #232: playerIds who have spent **Sneak Peek** on this clue (one peek per clue). */
  peekedBy?: string[];
  /**
   * #232 v2 — playerIds who armed **Brain Freeze** (pre-buzz). At buzzer-open every
   * OTHER seat's buzz is delayed by `freezeMs` (see {@link buzzOffsetsMs}); the frozen
   * seats are notified (open, not stealth). Refunded if the caster never wins the buzz.
   */
  brainFreezeBy?: string[];
  /**
   * #232 v2 — UNRESOLVED armed **Snake Bite** casts (pre-buzz, stealth + targeted).
   * Snake Bite is a hard PREVENT, not a slow: the target's buzz is blocked for the clue.
   * A cast stays here until it resolves — it LANDS (moves to {@link snakeBittenPlayerIds}
   * + consumes the caster's card) the moment its target tries to buzz, or is refunded to
   * the caster in `consumeClue` if the target never buzzes (they never learn). Stealth:
   * redacted from every non-host view so no one sees a bite coming.
   */
  snakeBiteBy?: { casterId: string; targetId: string }[];
  /**
   * #232 v2 — targets a landed Snake Bite has BLOCKED for this clue (they tried to buzz
   * and were bitten). The server rejects their buzzes for the rest of the clue. Redacted
   * from non-host views (the target learns via the `snake_bite_revealed` event, and the
   * room is never told).
   */
  snakeBittenPlayerIds?: string[];
  /**
   * #232 v2 — per-seat buzz delay (ms), COMPUTED at buzzer-open from the armed
   * **Brain Freeze** casts (a slow, not a block — Snake Bite is a hard prevent handled
   * separately). Its PRESENCE (a non-empty map) switches `handleBuzz` from the immediate
   * arrival-order fast path to the buffer-and-compare window; absent/empty ⇒ zero added
   * latency (the base case).
   */
  buzzOffsetsMs?: Record<string, number>;
  /** #232 v2 — playerIds who armed **Insurance** (pre-buzz): a wrong-answer loss is halved. */
  insuredBy?: string[];
  /**
   * #232 v2 — set once the buzzer has REOPENED after a wrong answer. **Steal** is legal
   * only while this is true (front-of-queue on the rebound), never on the initial open.
   */
  reopened?: boolean;
  /** #232 v2 — playerIds who spent **Tag-Team** on this clue (post-buzz; one per player). */
  tagTeamBy?: string[];
  /**
   * #232 v2 — the teammate each Tag-Team caster tapped on this clue (`casterId →
   * teammateId`). Records who to pay the helper bonus to: when the caster (the buzzer
   * holder) answers correctly, their tagged teammate banks a share of the clue value.
   */
  tagTeamPartner?: Record<string, string>;
  /** #232 v2 — playerIds who armed **Wager Boost** on this clue's Daily Double wager step. */
  wagerBoostBy?: string[];
  /**
   * #232 v2 — the **Wager Boost** win multiplier rolled once the wager was locked (e.g.
   * `2` = double the winnings). Applied to a CORRECT Daily Double payout only (a wrong
   * answer loses the plain wager). Persisted so recovery never re-rolls.
   */
  wagerBoostMult?: number;
}

/** NEW (DESIGN §4.1): Final Jeopardy round state. */
export interface FinalJeopardyState {
  category: string;
  /** playerId -> wager (secret until that player's reveal step). */
  wagers: Record<string, number>;
  /** playerId -> typed answer (secret until reveal). */
  answers: Record<string, string>;
  /** #232 v2 — playerIds who armed **Wager Boost** for their Final wager. */
  wagerBoostBy?: string[];
  /**
   * #232 v2 — playerId -> **Wager Boost** win multiplier rolled once that player's Final
   * wager was locked. Multiplies a CORRECT Final payout only. Persisted (never re-rolled).
   */
  wagerBoostMult?: Record<string, number>;
  /** #232 v2 — playerIds who armed **Insurance** for the Final clue (loss halved). */
  insuredBy?: string[];
  /** Players sorted ascending by score at final start (judging order, FR-6.4). */
  judgingOrder: string[];
  judgedPlayerIds: string[];
  /**
   * Correct-response reveal hold (#205, FR-6.6). Present ONLY while
   * `phase === 'FINAL_REVEAL'`: after the LAST player is judged, the game lingers on
   * the judged state (correct response revealed to all — see `views.ts`) for
   * `GameRules.finalRevealHoldSec` before GAME_OVER. `resolvesAtEpochMs` drives the
   * client "final results in {n}s" countdown and lets recovery re-arm the remaining
   * hold. Mirrors {@link ClueReveal.resolvesAtEpochMs} for the board-clue reveal.
   */
  reveal?: { resolvesAtEpochMs: number };
  /**
   * Correct-response suspense gate (#344, FR-6.6 amended). During `FINAL_JUDGING` the
   * correct response is HELD from the room and revealed only when the **first** contestant
   * is judged correct — this flag flips `true` at that moment (`applyFinalJudge`) and is
   * **monotonic** (never reset), so the response stays revealed for the rest of judging.
   * The all-wrong case needs no flag: `FINAL_REVEAL` reveals unconditionally by phase (see
   * `views.ts` `finalResponseRevealed`). Unset/`false` before the first correct ruling.
   * Non-secret (it only says "the response is now public"), so it rides the wire. Persisted
   * on the actor snapshot, so a mid-judging recovery preserves it (no re-arm needed).
   */
  responseRevealed?: boolean;
}

/**
 * Final Peril clue prompt + correct response, persisted onto the end-state so the
 * GAME_OVER standings screen keeps showing them (#205, FR-6.6 / DI-C). Set by the
 * actor when a game that played Final Peril ends; the pack-snapshot un-blank of the
 * response only lasts through the reveal (`FINAL_JUDGING`/`FINAL_REVEAL`), so this
 * explicit field is what survives the transition to GAME_OVER. Present ONLY at
 * GAME_OVER (the game is over, so it carries no live secret) and absent when a game
 * ends before Final Peril was played.
 */
export interface FinalOutcome {
  category: string;
  prompt: string;
  response: string;
}

/**
 * Server-authoritative timer state (DESIGN §4.1 / §7). Remaining time is
 * reconstructed on recovery from `startedAtEpochMs`/`pausedAtEpochMs` + wall clock.
 */
export interface TimerState {
  timeRemaining: number;
  isActive: boolean;
  isPaused: boolean;
  hasExpired: boolean;
  clueId: string | null;
  duration: number;
  startedAtEpochMs: number | null;
  pausedAtEpochMs: number | null;
}

/**
 * Mode-generic core of the authoritative game model (#257 S3, D-S3.2). Holds everything
 * that is NOT specific to one game mode's rules: identity, roster, the shared timer, the
 * lifecycle, resolved rules, and the pre-play lobby/generation surfaces. A mode's own state
 * lives in the discriminated {@link GameState} arm (`jeopardy` today; `quiz` in S4).
 */
export interface GameStateCore {
  gameId: string;
  joinCode: string;
  hostUserId: string;
  /** How this game's board is sourced (M12). Fixed at creation. */
  mode: BoardSource;
  /**
   * Who runs the game (#91, FR-11.1). Fixed at creation; default `HUMAN`. `AUTO`
   * engages the in-actor auto-host (reads clues, opens buzzers, LLM-judges spoken
   * answers). Persisted so recovery rebuilds identical behavior.
   */
  hostMode: HostMode;
  /**
   * Resolved play-time rules for this game (see `GameRules`). Fixed at creation
   * and persisted with the game so recovery (M4) rebuilds identical behavior.
   */
  rules: GameRules;
  /**
   * The per-game rule overrides supplied at creation (e.g. the FR-4.11
   * buzzer-smash opt-in, an operator timer override). Persisted so they can be
   * RE-APPLIED whenever `rules` is re-resolved — notably when a DYNAMIC game's
   * generated pack is swapped in (`GENERATING → IN_ROUND`), which recomputes
   * `rules` from the pack and would otherwise drop the creation-time overrides
   * (#198: smash-lockout silently reverting to `ignore` after generation).
   */
  rulesOverrides?: GameRulesOverrides;
  /**
   * Mode-generic game lifecycle (#257 S3, D-S3.3). The split-out of the old flat
   * `phase`'s lifecycle values; the mode owns the fine-grained play sub-phase (e.g.
   * `jeopardy.phase`). Use {@link gamePhaseOf}/{@link setGamePhase} to read/write the
   * combined flat phase, {@link mapLegacyPhase} for the mapping.
   */
  lifecycle: GameLifecycle;
  /** 1..8 players (FR-2.5). */
  players: Player[];
  /**
   * Pre-play lobby state (M12). Present only in `LOBBY`/`GENERATING`; holds each
   * player's category-preference submission (dynamic mode) and roster context.
   */
  lobby?: LobbyState;
  /** In-flight generation progress (M12). Present only in `GENERATING`. */
  generation?: GenerationState;
  timer: TimerState;
  /** Monotonic; every mutation increments (DESIGN §5.3). */
  version: number;
  updatedAtEpochMs: number;
  /**
   * Persistence schema marker (#257 S3, D-S3.5). Set to `2` by `serializeState`; absent on
   * live state and on pre-S3 (flat) snapshots — the migration shim keys off its absence.
   */
  schemaVersion?: number;
}

/**
 * The Jeopardy mode's per-game state (#257 S3, D-S3.2) — the strictly-Jeopardy fields split
 * out of the old flat `GameState`. Lives under `GameState.jeopardy` on the `JEOPARDY` arm.
 */
export interface JeopardyModeState {
  /**
   * Jeopardy play sub-phase (#257 S3, D-S3.3). Present ONLY while the core
   * {@link GameStateCore.lifecycle} is `IN_PLAY`; undefined in LOBBY/GENERATING/GAME_OVER.
   */
  phase?: JeopardyPhase;
  packSnapshot: PackSnapshot;
  currentRound: Round;
  /** Active round's board (derived from packSnapshot). */
  board: Board;
  /** Who picks the next clue (the correct answerer — FR-4.5, rule nuance #7). */
  boardControlPlayerId?: string;
  /** Present when a clue is open. */
  clueState?: ClueState;
  /**
   * Auto-host (#91) — the most recent LLM ruling, retained so a Dispute can
   * target it even after the clue is consumed. Present only in `AUTO` mode.
   */
  lastAutoRuling?: AutoRuling;
  /**
   * Auto-host (#91, D8) — an open Dispute vote over `lastAutoRuling`. Present only
   * while a vote is in progress (`AUTO` mode).
   */
  dispute?: DisputeState;
  /** Present during Final Jeopardy. */
  final?: FinalJeopardyState;
  /**
   * Power-ups config + deterministic comeback roll state (#232). Present when
   * power-ups are enabled for this game; part of the snapshot so recovery rebuilds
   * identical behavior (and never re-rolls). Absent = Classic (no power-ups).
   */
  powerups?: PowerUpGameState;
  /**
   * Final Peril clue + correct response, kept for the GAME_OVER standings screen
   * (#205, FR-6.6). Set only when a game that reached Final Peril ends; absent
   * otherwise. See {@link FinalOutcome}.
   */
  finalOutcome?: FinalOutcome;
  /**
   * Zero-commitment demo marker (#246, FR-17). Present ONLY on a per-visitor demo
   * game; its presence is the authoritative "this is a demo" flag the actor keys
   * off to skip the paid-path charge (FR-17.1), suppress leaderboard/history writes
   * (FR-17.3), and auto-end at the box (FR-17.4). Fixed at creation and persisted so
   * recovery rebuilds identical behavior. Carried on the wire so every participant
   * renders the demo ribbon + convert CTA consistently. Absent = a normal game.
   */
  demo?: DemoConfig;
}

/**
 * The full authoritative game model held in memory by the owning actor (#257 S3): the
 * mode-generic {@link GameStateCore} intersected with a discriminated per-mode arm. Today
 * the only mode is `JEOPARDY`; S4 adds `| (GameStateCore & { modeId: 'TIMED_QUIZ'; quiz: … })`.
 */
export type GameState = GameStateCore & {
  modeId: GameModeId;
  jeopardy: JeopardyModeState;
};

/** Final standings entry (computed on GAME_OVER, FR-1.6). */
export interface Standing {
  playerId: string;
  name: string;
  score: number;
  /** Rank (1-based); tied players share a rank (co-winners — FR-4.6). */
  rank: number;
  /** Profile picture URL, carried from {@link Player.avatarUrl} (#42, FR-2.10). */
  avatarUrl?: string;
}
